import { Form, Head, Link, router } from "@inertiajs/react"
import {
  Camera,
  Check,
  ExternalLink,
  Pencil,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react"
import { useState } from "react"

import { StatusPill } from "@/components/directory/status-pill"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import AppLayout from "@/layouts/app-layout"
import { cn } from "@/lib/utils"
import {
  adminSitePath,
  adminSitesPath,
  approveAdminSitePath,
  recaptureAdminSitePath,
  recheckAdminSitePath,
  rejectAdminSitePath,
  sitePath,
} from "@/routes"
import type { AdminSite, Pagination } from "@/types/site"

type Tab = "pending" | "screenshots" | "down" | "all" | "rejected"

interface Props {
  tab: Tab
  q: string
  sites: AdminSite[]
  pagination: Pagination
  counts: Record<Tab, number>
  worker: {
    last_check_at: string | null
    checks_last_hour: number
    screenshots_last_day: number
    queued_screenshots: number
    failed_jobs: number
    stale: boolean
  }
}

const TABS: { value: Tab; label: string }[] = [
  { value: "pending", label: "Pending" },
  { value: "screenshots", label: "Screenshot issues" },
  { value: "down", label: "Down / gone" },
  { value: "all", label: "Listed" },
  { value: "rejected", label: "Rejected" },
]

function ago(iso: string | null) {
  if (!iso) return "never"
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  return hours < 48 ? `${hours}h ago` : `${Math.round(hours / 24)}d ago`
}

export default function AdminSites({
  tab,
  q,
  sites,
  pagination,
  counts,
  worker,
}: Props) {
  const stale = worker.stale

  return (
    <AppLayout breadcrumbs={[{ title: "Moderation", href: adminSitesPath() }]}>
      <Head title="Moderation" />
      <div className="space-y-6 p-4 sm:p-6">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
          <Metric
            label="Last check"
            value={ago(worker.last_check_at)}
            warn={stale}
            hint={stale ? "Is the worker service running?" : undefined}
          />
          <Metric
            label="Checks, last hour"
            value={worker.checks_last_hour.toLocaleString()}
          />
          <Metric
            label="Screenshots, 24h"
            value={worker.screenshots_last_day.toLocaleString()}
          />
          <Metric
            label="Screenshots queued"
            value={worker.queued_screenshots.toLocaleString()}
          />
          <Metric
            label="Failed jobs"
            value={worker.failed_jobs.toLocaleString()}
            warn={worker.failed_jobs > 0}
          />
        </div>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="bg-muted inline-flex flex-wrap rounded-lg p-1">
            {TABS.map((item) => (
              <Link
                key={item.value}
                href={adminSitesPath({ tab: item.value })}
                preserveScroll
                className={cn(
                  "rounded-md px-3 py-1.5 text-sm font-medium",
                  tab === item.value
                    ? "bg-card shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {item.label}
                <span className="text-muted-foreground ml-1.5 font-mono text-xs">
                  {counts[item.value]}
                </span>
              </Link>
            ))}
          </div>
          <form
            className="lg:ml-auto"
            onSubmit={(event) => {
              event.preventDefault()
              const data = new FormData(event.currentTarget)
              router.get(
                adminSitesPath(),
                { tab, q: (data.get("q") as string | null) ?? "" },
                { preserveState: true },
              )
            }}
          >
            <Input
              name="q"
              defaultValue={q}
              placeholder="Search"
              className="w-64"
            />
          </form>
        </div>

        {sites.length === 0 ? (
          <p className="text-muted-foreground rounded-xl border border-dashed py-16 text-center">
            Nothing here. Nice.
          </p>
        ) : (
          <ul className="divide-y rounded-xl border">
            {sites.map((site) => (
              <SiteRow key={site.id} site={site} />
            ))}
          </ul>
        )}

        {pagination.total > pagination.per_page && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              Page {pagination.page} of{" "}
              {Math.ceil(pagination.total / pagination.per_page)}
            </span>
            <div className="flex gap-2">
              {pagination.page > 1 && (
                <Button variant="outline" size="sm" asChild>
                  <Link
                    href={adminSitesPath({ tab, q, page: pagination.page - 1 })}
                  >
                    Previous
                  </Link>
                </Button>
              )}
              {pagination.page * pagination.per_page < pagination.total && (
                <Button variant="outline" size="sm" asChild>
                  <Link
                    href={adminSitesPath({ tab, q, page: pagination.page + 1 })}
                  >
                    Next
                  </Link>
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  )
}

function Metric({
  label,
  value,
  warn,
  hint,
}: {
  label: string
  value: string
  warn?: boolean
  hint?: string
}) {
  return (
    <div
      className={cn(
        "rounded-xl border p-4",
        warn && "border-status-down/50 bg-status-down/5",
      )}
    >
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="mt-1 text-xl font-bold">{value}</p>
      {hint && <p className="text-status-down mt-1 text-xs">{hint}</p>}
    </div>
  )
}

function SiteRow({ site }: { site: AdminSite }) {
  const [editing, setEditing] = useState(false)
  const post = (href: string, data: Record<string, string> = {}) =>
    router.post(href, data, { preserveScroll: true })

  return (
    <li className="flex flex-col gap-4 p-4 md:flex-row">
      <a href={site.url} target="_blank" rel="noreferrer" className="shrink-0">
        {site.thumbnail_url ? (
          <img
            src={site.thumbnail_url}
            alt=""
            width={192}
            height={120}
            loading="lazy"
            className="aspect-[16/10] w-48 rounded-lg border object-cover object-top"
          />
        ) : (
          <div className="bg-muted text-muted-foreground flex aspect-[16/10] w-48 items-center justify-center rounded-lg border text-xs">
            {site.screenshot_queued ? "Capturing…" : "No screenshot"}
          </div>
        )}
      </a>

      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="truncate font-semibold">{site.title}</h3>
          <StatusPill status={site.status} />
          {site.moderation_state !== "approved" && (
            <span className="bg-brand-soft text-brand rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase">
              {site.moderation_state}
            </span>
          )}
          {site.screenshot_source === "wayback" && (
            <span className="text-muted-foreground text-xs">wayback shot</span>
          )}
        </div>
        <a
          href={site.url}
          target="_blank"
          rel="noreferrer"
          className="text-muted-foreground inline-flex items-center gap-1 font-mono text-xs hover:underline"
        >
          {site.host} <ExternalLink className="size-3" />
        </a>
        {site.description && (
          <p className="text-muted-foreground line-clamp-2 text-sm">
            {site.description}
          </p>
        )}
        <div className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-xs">
          <span>source: {site.source}</span>
          <span>checked {ago(site.last_checked_at)}</span>
          {site.uptime_30d !== null && <span>uptime {site.uptime_30d}%</span>}
          {site.last_error && (
            <span className="text-status-down">{site.last_error}</span>
          )}
          {site.screenshot_error && (
            <span className="text-status-down">
              screenshot: {site.screenshot_error}
            </span>
          )}
          {site.moderation_note && <span>note: {site.moderation_note}</span>}
          {site.submitter_email && <span>from {site.submitter_email}</span>}
        </div>

        {editing && (
          <Form
            method="patch"
            action={adminSitePath(site.slug)}
            options={{ preserveScroll: true }}
            onSuccess={() => setEditing(false)}
            className="grid gap-2 pt-2 sm:grid-cols-2"
          >
            {({ errors, processing }) => (
              <>
                <Input
                  name="title"
                  defaultValue={site.title}
                  placeholder="Title"
                />
                <Input
                  name="author_name"
                  defaultValue={site.author_name ?? ""}
                  placeholder="Author"
                />
                <Input
                  name="description"
                  defaultValue={site.description ?? ""}
                  placeholder="Description"
                  className="sm:col-span-2"
                />
                <Input
                  name="author_url"
                  defaultValue={site.author_url ?? ""}
                  placeholder="Author URL"
                />
                <Input
                  name="launched_on"
                  type="date"
                  defaultValue={site.launched_on ?? ""}
                />
                <InputError
                  message={errors.title ?? errors.description}
                  className="sm:col-span-2"
                />
                <div className="flex gap-2 sm:col-span-2">
                  <Button size="sm" disabled={processing}>
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    type="button"
                    onClick={() => setEditing(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </Form>
        )}
      </div>

      <div className="flex shrink-0 flex-wrap items-start gap-2 md:w-56 md:justify-end">
        {site.moderation_state !== "approved" && (
          <Button
            size="sm"
            onClick={() => post(approveAdminSitePath(site.slug))}
          >
            <Check /> Approve
          </Button>
        )}
        {site.moderation_state !== "rejected" && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const note = window.prompt(`Reject ${site.host}? Optional note:`)
              if (note !== null) post(rejectAdminSitePath(site.slug), { note })
            }}
          >
            <X /> Reject
          </Button>
        )}
        <Button
          size="icon-sm"
          variant="ghost"
          title="Edit"
          onClick={() => setEditing((value) => !value)}
        >
          <Pencil />
        </Button>
        <Button
          size="icon-sm"
          variant="ghost"
          title="Re-check now"
          onClick={() => post(recheckAdminSitePath(site.slug))}
        >
          <RefreshCw />
        </Button>
        <Button
          size="icon-sm"
          variant="ghost"
          title="New screenshot"
          onClick={() => post(recaptureAdminSitePath(site.slug))}
        >
          <Camera />
        </Button>
        {site.moderation_state === "approved" && (
          <Button
            size="icon-sm"
            variant="ghost"
            title="View public page"
            asChild
          >
            <Link href={sitePath(site.slug)}>
              <ExternalLink />
            </Link>
          </Button>
        )}
        <Button
          size="icon-sm"
          variant="ghost"
          title="Delete"
          onClick={() => {
            if (window.confirm(`Delete ${site.host} and its history?`)) {
              router.delete(adminSitePath(site.slug), { preserveScroll: true })
            }
          }}
        >
          <Trash2 />
        </Button>
      </div>
    </li>
  )
}
